import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const INSTRUCTOR_1_PHOTO =
  "https://static.readdy.ai/image/99ee4fd5eb46fc3e1cae300823aab6a6/d0af4d7d162fec6e5a578befc0a6cad3.jpeg";
const INSTRUCTOR_2_PHOTO =
  "https://public.readdy.ai/ai/img_res/edited_7a89f4dd0b1108434df44def4d8b32ff_d8851023.jpg";

interface Instructor {
  id: number;
  photo: string;
  name: string;
  role: string;
  quote: string;
  bio: string;
  credentials: string[];
  specialty: string;
  expanded: boolean;
}

const instructors: Instructor[] = [
  {
    id: 1,
    photo: INSTRUCTOR_1_PHOTO,
    name: "Destiny",
    role: "Registered Nurse | Real Estate Agent | CPR Instructor",
    quote: "I became a CPR instructor because I\u2019ve seen what happens when people aren\u2019t prepared. My mission is simple: make sure every student leaves here confident, certified, and ready to save a life.",
    bio: "With hands-on clinical experience in ICU and emergency settings, Destiny created Cardiocraft CPR to bring hospital-grade life support training directly to the Kissimmee community. Every class is designed with the depth and situational awareness that only real-world nursing delivers.",
    credentials: ["RN Licensed", "AHA-Aligned Instructor", "BLS / ACLS / PALS", "ICU & ER Experience"],
    specialty: "BLS \u00b7 ACLS \u00b7 PALS \u00b7 Heartsaver",
    expanded: false,
  },
  {
    id: 2,
    photo: INSTRUCTOR_2_PHOTO,
    name: "Nathaniel",
    role: "Registered Nurse | CPR Instructor",
    quote: "Every skill I teach comes straight from the bedside. When you train with me, you\u2019re learning exactly what works when it matters most.",
    bio: "Nathaniel brings years of frontline nursing experience to every Cardiocraft CPR class. His calm, clear instruction style makes even the most technical life-support protocols approachable for every student \u2014 from first-timers to seasoned healthcare professionals.",
    credentials: ["RN Licensed", "AHA-Aligned Instructor", "BLS / Heartsaver", "Clinical Care Expert"],
    specialty: "BLS \u00b7 Heartsaver CPR \u00b7 First Aid",
    expanded: false,
  },
];

function InstructorCard({ instructor, index, visible }: { instructor: Instructor; index: number; visible: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative flex flex-col rounded-2xl overflow-hidden cursor-pointer transition-all duration-700"
      style={{
        background: "linear-gradient(160deg, #1a0a2e 0%, #0f0d1a 100%)",
        border: hovered
          ? "1px solid rgba(212,175,55,0.6)"
          : "1px solid rgba(212,175,55,0.2)",
        boxShadow: hovered
          ? "0 0 40px rgba(107,70,193,0.25), 0 0 20px rgba(212,175,55,0.1)"
          : "none",
        opacity: visible ? 1 : 0,
        transform: visible
          ? "translateY(0)"
          : `translateY(${index === 0 ? "-" : ""}40px)`,
        transitionDelay: `${index * 180}ms`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Photo area */}
      <div
        className="relative overflow-hidden"
        style={{ height: "400px" }}
      >
        <img
          src={instructor.photo}
          alt={`${instructor.name} — RN Instructor, Cardiocraft CPR Kissimmee`}
          className="w-full h-full object-cover object-center transition-transform duration-700"
          style={{ transform: hovered ? "scale(1.05)" : "scale(1)" }}
        />
        {/* Gradient overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to top, rgba(13,9,26,0.95) 0%, rgba(13,9,26,0.3) 55%, transparent 100%)",
          }}
        />
        {/* Purple glow on hover */}
        {hovered && (
          <div
            className="absolute inset-0 pointer-events-none transition-opacity duration-500"
            style={{
              background: "radial-gradient(ellipse at center bottom, rgba(107,70,193,0.18) 0%, transparent 70%)",
            }}
          />
        )}

        {/* AHA badge */}
        <div
          className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{
            background: "linear-gradient(135deg, #6B46C1, #4C1D95)",
            border: "1px solid rgba(212,175,55,0.5)",
            boxShadow: "0 2px 12px rgba(107,70,193,0.4)",
          }}
        >
          <i className="ri-shield-check-fill text-brand-gold text-xs" />
          <span className="text-white text-xs font-bold whitespace-nowrap">AHA Instructor</span>
        </div>

        {/* Stars */}
        <div
          className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1.5 rounded-full"
          style={{
            background: "rgba(212,175,55,0.12)",
            border: "1px solid rgba(212,175,55,0.3)",
            backdropFilter: "blur(8px)",
          }}
        >
          {[1, 2, 3, 4, 5].map((s) => (
            <i key={s} className="ri-star-fill text-brand-gold text-xs" />
          ))}
        </div>

        {/* Name + role over photo bottom */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-5">
          <h3 className="font-serif font-bold text-xl text-white mb-0.5">{instructor.name}</h3>
          <p className="text-brand-gold text-xs font-bold uppercase tracking-wider">{instructor.role}</p>
        </div>
      </div>

      {/* Content area */}
      <div className="flex flex-col flex-1 px-6 pt-5 pb-6 gap-4">
        {/* Quote */}
        <blockquote
          className="pl-4 italic text-white/75 text-sm leading-relaxed"
          style={{ borderLeft: "2px solid rgba(212,175,55,0.7)" }}
        >
          &ldquo;{instructor.quote}&rdquo;
        </blockquote>

        {/* Specialty */}
        <div className="flex items-center gap-2">
          <i className="ri-stethoscope-line text-brand-gold text-sm" />
          <span className="text-white/50 text-xs font-medium">{instructor.specialty}</span>
        </div>

        {/* Credential badges */}
        <div className="flex flex-wrap gap-2">
          {instructor.credentials.map((cred) => (
            <span
              key={cred}
              className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
              style={{
                background: "rgba(107,70,193,0.2)",
                border: "1px solid rgba(107,70,193,0.4)",
                color: "rgba(255,255,255,0.85)",
              }}
            >
              {cred}
            </span>
          ))}
        </div>

        {/* Expand bio toggle */}
        <button
          type="button"
          className="flex items-center gap-2 text-brand-gold text-xs font-bold uppercase tracking-wide hover:underline cursor-pointer w-fit transition-all duration-200"
          onClick={() => setExpanded(!expanded)}
        >
          <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`} />
          {expanded ? "Hide Bio" : "View Full Bio"}
        </button>

        {/* Expandable bio */}
        <div
          className="overflow-hidden transition-all duration-500"
          style={{ maxHeight: expanded ? "200px" : "0px", opacity: expanded ? 1 : 0 }}
        >
          <p className="text-white/60 text-sm leading-relaxed">{instructor.bio}</p>
        </div>
      </div>
    </div>
  );
}

export default function InstructorsDuo() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-24 overflow-hidden"
      style={{ background: "linear-gradient(160deg, #0d0d1a 0%, #1a0a2e 50%, #0f0f0f 100%)" }}
    >
      {/* Top gold divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-40" />
      {/* Bottom gold divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-40" />

      {/* Background glow orbs */}
      <div
        className="absolute left-0 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(107,70,193,0.12) 0%, transparent 70%)" }}
      />
      <div
        className="absolute right-0 top-1/3 w-64 h-64 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.07) 0%, transparent 70%)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          className="text-center mb-16 transition-all duration-700"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(-30px)",
          }}
        >
          <div
            className="inline-flex items-center gap-2 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-5"
            style={{
              background: "rgba(212,175,55,0.08)",
              border: "1px solid rgba(212,175,55,0.3)",
            }}
          >
            <i className="ri-nurse-line" />
            Your Instructors
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Meet Your{" "}
            <span
              style={{
                background: "linear-gradient(90deg, #D4AF37, #f5d97a, #D4AF37)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              RN Instructors
            </span>
          </h2>
          <p className="text-white/60 text-base max-w-2xl mx-auto leading-relaxed">
            Train with confidence under the guidance of experienced Registered Nurses. At Cardiocraft CPR, our instructors bring real-world medical expertise, hands-on training, and a commitment to excellence. Our RN-led courses ensure you not only get certified, but gain the confidence and skills to respond in real-life emergencies.
          </p>
        </div>

        {/* Dual instructor cards */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {instructors.map((instructor, index) => (
            <InstructorCard
              key={instructor.id}
              instructor={instructor}
              index={index}
              visible={visible}
            />
          ))}
        </div>

        {/* Trust strip */}
        <div
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto transition-all duration-700 delay-500"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)" }}
        >
          {[
            { icon: "ri-medal-2-line", label: "AHA Aligned" },
            { icon: "ri-time-line", label: "Same-Day Cert" },
            { icon: "ri-nurse-line", label: "RN Instructors" },
            { icon: "ri-heart-pulse-line", label: "500+ Trained" },
          ].map((item) => (
            <div
              key={item.label}
              className="flex flex-col items-center gap-2 py-4 px-3 rounded-xl"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(212,175,55,0.15)",
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ background: "rgba(107,70,193,0.25)" }}>
                <i className={`${item.icon} text-brand-gold text-lg`} />
              </div>
              <span className="text-white/70 text-xs font-semibold text-center">{item.label}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className="text-center mt-12 transition-all duration-700 delay-600"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)" }}
        >
          <Link
            to="/booking#reserve-spot"
            className="inline-flex items-center gap-2 font-bold text-sm px-8 py-4 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-300 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #D4AF37, #b8952e)",
              color: "#1A1A1A",
              boxShadow: "0 4px 24px rgba(212,175,55,0.35)",
            }}
          >
            <i className="ri-calendar-check-line" />
            Book a Class with Our RN Team
          </Link>
        </div>
      </div>
    </section>
  );
}
